import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { recordRoute } from './utils/routeHistory'
import React, { useEffect, useState } from 'react'
import { CircularProgress, Box } from '@mui/material'

// Auth Components
import RoleGuard from './components/RoleGuard'
import FpUserGuard from './components/FpUserGuard'
import Login from './pages/user/Login'
import { useAuth } from './contexts/AuthContext'

// Layouts
import AdminLayout from './layouts/AdminLayout'
import InspectorLayout from './layouts/InspectorLayout'

// Standalone Pages
import FPDirectory from './pages/FPDirectory'

// Admin Pages
import SystemStatus from './pages/admin/system/SystemStatus'
import FranchisePartners from './pages/admin/FranchisePartners'
import FranchisePartnerForm from './pages/admin/FranchisePartnerForm'
import ManageConnections from './pages/admin/ManageConnections'
import SystemConnections from './pages/admin/SystemConnections'
import FeedbackAdminPage from './pages/admin/Feedback'
import DataSyncRoutes from './pages/admin/DataSync/Routes'
import DataExceptionsLayout from './pages/admin/DataExceptions/DataExceptionsLayout'
import DataExceptionsOverview from './pages/admin/DataExceptions/tabs/Overview'
import DataExceptionsFpGrid from './pages/admin/DataExceptions/tabs/FpGrid'
import DataExceptionsTrending from './pages/admin/DataExceptions/tabs/Trending'
import DataExceptionsNotes from './pages/admin/DataExceptions/tabs/Notes'
import DataExceptionsReports from './pages/admin/DataExceptions/tabs/Reports'
import ExceptionSetup from './pages/admin/ExceptionSetup'
import ExceptionTypeForm from './pages/admin/ExceptionTypeForm'
import HealthScores from './pages/admin/HealthScores'
import HealthScoreDetails from './pages/admin/HealthScoreDetails'
import Settings from './pages/admin/Settings'
import PwxMasters from './pages/admin/PwxMasters'
import XeroTenantSelection from './pages/admin/XeroTenantSelection'
import Users from './pages/admin/Users'
import UserForm from './pages/admin/UserForm'
import AuditLogs from './pages/admin/AuditLogs'
import UserActivity from './pages/admin/UserActivity'
import BDMList from './pages/admin/BDMList'
import BDMForm from './pages/admin/BDMForm'
import RegionList from './pages/admin/RegionList'
import RegionForm from './pages/admin/RegionForm'
import PlatformReportsRoutes from './pages/admin/PlatformReports/Routes'
import SystemAdminTools from './pages/admin/SystemAdminTools'
import BulkOperationsPage from './pages/admin/SystemAdminTools/BulkOperationsPage'
import IntegrationsLanding from './pages/admin/system/Integrations'
import FreshdeskLayout from './pages/admin/system/Integrations/Freshdesk/FreshdeskLayout'
import FreshdeskStatus from './pages/admin/system/Integrations/Freshdesk/FreshdeskStatus'
import FreshdeskSolutionsTab from './pages/admin/system/Integrations/Freshdesk/FreshdeskSolutionsTab'
import FreshdeskCompaniesTab from './pages/admin/system/Integrations/Freshdesk/FreshdeskCompaniesTab'
import ConfluenceStatus from './pages/admin/system/Integrations/Confluence/ConfluenceStatus'
import InvitationPage from './pages/user/InvitationPage'
import InvitationLanding from './pages/user/InvitationLanding'
import RegistrationSuccess from './pages/user/RegistrationSuccess'
import ChooseFranchise from './pages/user/ChooseFranchise'
import ReleaseHistory from './pages/user/ReleaseHistory'
import NotFound from './pages/NotFound'

// Inspector Pages
import InspectorDashboard from './pages/inspector/InspectorDashboard'
// import InspectorSales from './pages/inspector/Sales' // OLD: Replaced by new modular structure
// import SalesReports from './pages/inspector/Sales/Reports' // OLD: Replaced by new modular structure
// import SalesTools from './pages/inspector/Sales/Tools' // OLD: Functionality moved to new Tools hub
// import InspectorProducts from './pages/inspector/Inventory' // OLD: Replaced by new modular structure
// import ProductsInventory from './pages/inspector/Inventory/Inventory' // OLD: Moved to Data Explorer
// import ProductsReports from './pages/inspector/Inventory/Reports' // OLD: Replaced by new modular structure
// import InventoryTools from './pages/inspector/Inventory/Tools' // OLD: Moved to Tools hub
// import CogsAccountReview from './pages/inspector/Inventory/CogsAccountReview' // OLD: Moved to Tools hub
import InspectorClients from './pages/inspector/Clients'
import InspectorAccounts from './pages/inspector/Accounts'
// import InspectorSystems from './pages/inspector/Systems' // OLD: Replaced by Support hub

// New Inspector Components
import ExceptionsRoutes from './pages/inspector/Exceptions/Routes'
import ToolsRoutes from './pages/inspector/Tools/Routes'
import DataExplorer from './pages/inspector/DataExplorer'
import SupportHub from './pages/inspector/Support'
import HealthCheck from './pages/inspector/HealthCheck'

// Feature-based admin landing page resolution
const FEATURE_LANDING_ORDER: Array<[string, string]> = [
  ['franchise_partners', '/admin/franchise-partners'],
  ['data_exceptions', '/admin/data-exceptions'],
  ['health_scores', '/admin/health-scores'],
  ['platform_reports', '/admin/platform-reports'],
  ['data_sync', '/admin/data-sync/active'],
  ['user_management', '/admin/users'],
  ['system_admin', '/admin/system/status'],
]

const getAdminLandingPath = (features: string[]): string => {
  const landing = FEATURE_LANDING_ORDER.find(([key]) => features.includes(key))
  return landing ? landing[1] : '/admin/franchise-partners'
}

// Legacy URLs kept alive for bookmarks / stale email links.
// Add a tuple here when a route is removed. Destinations should enforce
// their own auth/role - redirects here are unconditional.
const LEGACY_REDIRECTS: Array<[string, string]> = [
  ['/admin/dashboard', '/admin'],
]

// Redirect /admin index to the user's first accessible feature
const AdminRedirect: React.FC = () => {
  const { user } = useAuth()
  const role = user?.role
  if (role === 'EVO_ADMIN') {
    return <Navigate to="/admin/franchise-partners" replace />
  }
  const features = user?.allowed_features ?? []
  return <Navigate to={getAdminLandingPath(features)} replace />
}

// Role-based redirect component
const RoleRedirect: React.FC = () => {
  const { user, loading: authLoading } = useAuth()
  const [redirectPath, setRedirectPath] = useState<string | null>(null)

  useEffect(() => {
    if (authLoading) return

    if (!user) {
      setRedirectPath('/login')
      return
    }

    const role = user.role
    if (role === 'EVO_ADMIN') {
      setRedirectPath('/admin/franchise-partners')
    } else if (role === 'EVO_TEAM') {
      const features = user.allowed_features ?? []
      setRedirectPath(features.length > 0 ? getAdminLandingPath(features) : '/choose-franchise')
    } else if (role === 'PSC_TEAM' || role === 'FP_USER') {
      setRedirectPath('/choose-franchise')
    } else {
      setRedirectPath('/login')
    }
  }, [user, authLoading])

  if (authLoading || !redirectPath) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  return <Navigate to={redirectPath} replace />
}

function App() {
  // Record each navigation so feedback can report the page the user came
  // from (see utils/routeHistory.ts). App is mounted inside BrowserRouter.
  const location = useLocation()
  useEffect(() => {
    recordRoute(location.pathname + location.search)
  }, [location.pathname, location.search])

  return (
    <Routes>
      {/* Inspector Tool Routes - Authentication-based access */}
      <Route path="/inspect/:fp_code" element={<InspectorLayout />}>
        <Route index element={<InspectorDashboard />} />
        
        {/* New restructured routes */}
        <Route path="health" element={<FpUserGuard kind="page" id="health_check"><HealthCheck /></FpUserGuard>} />
        <Route path="exceptions/*" element={<ExceptionsRoutes />} />
        <Route path="tools/*" element={<ToolsRoutes />} />
        <Route path="data/*" element={<FpUserGuard kind="page" id="data_explorer"><DataExplorer /></FpUserGuard>} />
        <Route path="support" element={<FpUserGuard kind="page" id="support"><SupportHub /></FpUserGuard>} />
        
        {/* Old routes - COMMENTED OUT: Replaced by new modular structure
        <Route path="sales" element={<Navigate to="list" replace />} />
        <Route path="sales/list" element={<InspectorSales />} />
        <Route path="sales/reports" element={<SalesReports />} />
        <Route path="sales/tools" element={<SalesTools />} />
        <Route path="inventory" element={<Navigate to="list" replace />} />
        <Route path="inventory/list" element={<InspectorProducts />} />
        <Route path="inventory/tools" element={<InventoryTools />} />
        <Route path="inventory/tools/cogs-account-review" element={<CogsAccountReview />} />
        <Route path="inventory/inventory" element={<ProductsInventory />} />
        <Route path="inventory/reports" element={<ProductsReports />} />
        <Route path="systems" element={<Navigate to="list" replace />} />
        <Route path="systems/list" element={<InspectorSystems />} />
        */}
        
        {/* Keep active until we confirm they're in new structure */}
        <Route path="clients" element={<FpUserGuard kind="page" id="clients"><InspectorClients /></FpUserGuard>} />
        <Route path="accounts" element={<FpUserGuard kind="page" id="accounts"><InspectorAccounts /></FpUserGuard>} />
      </Route>

      {/* Standalone Pages */}
      <Route path="/fp-directory" element={<FPDirectory />} />
      <Route path="/choose-franchise" element={<ChooseFranchise />} />
      <Route path="/releases" element={<ReleaseHistory />} />

      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/invitation/:fp_code/:token" element={<InvitationPage />} />
      <Route path="/invitation/:token" element={<InvitationLanding />} /> {/* Legacy support */}
      <Route path="/registration/success" element={<RegistrationSuccess />} />

      {/* Admin Panel Routes - Authenticated access */}
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminRedirect />} />
        {/* Franchise Partners */}
        <Route path="franchise-partners" element={<RoleGuard requiredFeature="franchise_partners"><FranchisePartners /></RoleGuard>} />
        <Route path="franchise-partners/new" element={<RoleGuard requiredFeature="franchise_partners"><FranchisePartnerForm /></RoleGuard>} />
        <Route path="franchise-partners/:id/edit" element={<RoleGuard requiredFeature="franchise_partners"><FranchisePartnerForm /></RoleGuard>} />
        <Route path="franchise-partners/:id/connections" element={<RoleGuard requiredFeature="franchise_partners"><ManageConnections /></RoleGuard>} />
        <Route path="system-connections" element={<RoleGuard requiredFeature="franchise_partners"><SystemConnections /></RoleGuard>} />
        <Route path="feedback" element={<RoleGuard requiredRole="team"><FeedbackAdminPage /></RoleGuard>} />

        {/* Data Sync */}
        <Route path="data-sync/*" element={<RoleGuard requiredFeature="data_sync"><DataSyncRoutes /></RoleGuard>} />

        {/* Data Exceptions - DQ Workbench (Overview / FP Grid / Trending / Reports) */}
        <Route path="data-exceptions" element={<RoleGuard requiredFeature="data_exceptions"><DataExceptionsLayout /></RoleGuard>}>
          <Route index element={<DataExceptionsOverview />} />
          <Route path="grid" element={<DataExceptionsFpGrid />} />
          <Route path="trending" element={<DataExceptionsTrending />} />
          <Route path="notes" element={<DataExceptionsNotes />} />
          <Route path="reports" element={<DataExceptionsReports />} />
        </Route>

        {/* Health Scores */}
        <Route path="health-scores" element={<RoleGuard requiredFeature="health_scores"><HealthScores /></RoleGuard>} />
        <Route path="health-scores/:fpCode" element={<RoleGuard requiredFeature="health_scores"><HealthScoreDetails /></RoleGuard>} />

        {/* User Management */}
        <Route path="users" element={<RoleGuard requiredFeature="user_management"><Users /></RoleGuard>} />
        <Route path="users/evo-team" element={<RoleGuard requiredFeature="user_management"><Users /></RoleGuard>} />
        <Route path="users/psc-team" element={<RoleGuard requiredFeature="user_management"><Users /></RoleGuard>} />
        <Route path="users/fp-users" element={<RoleGuard requiredFeature="user_management"><Users /></RoleGuard>} />
        <Route path="users/activity" element={<RoleGuard requiredFeature="user_management"><UserActivity /></RoleGuard>} />
        <Route path="users/new" element={<RoleGuard requiredFeature="user_management"><UserForm /></RoleGuard>} />
        <Route path="users/:id/edit" element={<RoleGuard requiredFeature="user_management"><UserForm /></RoleGuard>} />

        {/* Platform Reports */}
        <Route path="platform-reports/*" element={<RoleGuard requiredFeature="platform_reports"><PlatformReportsRoutes /></RoleGuard>} />

        {/* System */}
        <Route path="system/status" element={<RoleGuard requiredFeature="system_admin"><SystemStatus /></RoleGuard>} />
        <Route path="system/configuration" element={<RoleGuard requiredFeature="system_admin"><Settings /></RoleGuard>} />
        <Route path="system/audit-logs" element={<RoleGuard requiredFeature="system_admin"><AuditLogs /></RoleGuard>} />
        <Route path="system/exception-setup" element={<RoleGuard requiredFeature="system_admin"><ExceptionSetup /></RoleGuard>} />
        <Route path="system/exception-setup/new" element={<RoleGuard requiredFeature="system_admin"><ExceptionTypeForm /></RoleGuard>} />
        <Route path="system/exception-setup/:id/edit" element={<RoleGuard requiredFeature="system_admin"><ExceptionTypeForm /></RoleGuard>} />
        <Route path="system/pwx-masters" element={<RoleGuard requiredFeature="system_admin"><PwxMasters /></RoleGuard>} />
        <Route path="system/bdm" element={<RoleGuard requiredFeature="system_admin"><BDMList /></RoleGuard>} />
        <Route path="system/bdm/new" element={<RoleGuard requiredFeature="system_admin"><BDMForm /></RoleGuard>} />
        <Route path="system/bdm/:id/edit" element={<RoleGuard requiredFeature="system_admin"><BDMForm /></RoleGuard>} />
        <Route path="system/regions" element={<RoleGuard requiredFeature="system_admin"><RegionList /></RoleGuard>} />
        <Route path="system/regions/new" element={<RoleGuard requiredFeature="system_admin"><RegionForm /></RoleGuard>} />
        <Route path="system/regions/:code/edit" element={<RoleGuard requiredFeature="system_admin"><RegionForm /></RoleGuard>} />
        <Route path="system/admin-tools" element={<RoleGuard requiredFeature="system_admin"><SystemAdminTools /></RoleGuard>} />
        <Route path="system/admin-tools/bulk-operations" element={<RoleGuard requiredFeature="system_admin"><BulkOperationsPage /></RoleGuard>} />
        <Route path="system/integrations" element={<RoleGuard requiredFeature="system_admin"><IntegrationsLanding /></RoleGuard>} />
        <Route path="system/integrations/freshdesk" element={<RoleGuard requiredFeature="system_admin"><FreshdeskLayout /></RoleGuard>}>
          <Route index element={<FreshdeskStatus />} />
          <Route path="solutions" element={<FreshdeskSolutionsTab />} />
          <Route path="companies" element={<FreshdeskCompaniesTab />} />
          {/* Contacts / Tickets tabs added by downstream plans */}
        </Route>
        {/* Confluence: status-only foundation; a ConfluenceLayout + tabs lands
            with the content sync (phase 2). */}
        <Route path="system/integrations/confluence" element={<RoleGuard requiredFeature="system_admin"><ConfluenceStatus /></RoleGuard>} />

        {/* Redirect old report routes to system status */}
        <Route path="system/reports" element={<Navigate to="/admin/system/status" replace />} />
        <Route path="system/reports/daily" element={<Navigate to="/admin/system/status" replace />} />

        {/* Redirect /admin/system to configuration */}
        <Route path="system" element={<Navigate to="/admin/system/configuration" replace />} />
        {/* Legacy: old settings path now lives at /configuration */}
        <Route path="system/settings" element={<Navigate to="/admin/system/configuration" replace />} />

        {/* Redirects for old paths */}
        <Route path="admin-reports/*" element={<Navigate to="/admin/platform-reports" replace />} />
        <Route path="settings" element={<Navigate to="/admin/system/configuration" replace />} />
        <Route path="audit-logs" element={<Navigate to="/admin/system/audit-logs" replace />} />
        <Route path="exception-types" element={<Navigate to="/admin/system/exception-setup" replace />} />
        <Route path="exception-types/*" element={<Navigate to="/admin/system/exception-setup" replace />} />
        <Route path="bdm" element={<Navigate to="/admin/system/bdm" replace />} />
        <Route path="bdm/*" element={<Navigate to="/admin/system/bdm" replace />} />
        <Route path="regions" element={<Navigate to="/admin/system/regions" replace />} />
        <Route path="regions/*" element={<Navigate to="/admin/system/regions" replace />} />
        {/* Support team "switch to FP view" - generates inspector URL */}
        {/* <Route path="inspect/:fp_code" element={<AdminInspectorRedirect />} /> */}
      </Route>

      {/* Settings Routes - Outside admin layout for OAuth flows */}
      <Route path="/settings/connections/xero/select-tenant" element={<XeroTenantSelection />} />

      {/* Role-based landing page */}
      <Route path="/" element={<RoleRedirect />} />
      <Route path="/dashboard" element={<Navigate to="/admin/system/status" replace />} />
      <Route path="/franchise-partners" element={<Navigate to="/admin/franchise-partners" replace />} />
      <Route path="/sync-status" element={<Navigate to="/admin/data-sync/active" replace />} />
      <Route path="/admin/sync-status" element={<Navigate to="/admin/data-sync/active" replace />} />
      <Route path="/admin/sync-history" element={<Navigate to="/admin/data-sync/history" replace />} />
      <Route path="/admin/active-syncs" element={<Navigate to="/admin/data-sync/active" replace />} />

      {/* Legacy URL redirects - must come before the catch-all */}
      {LEGACY_REDIRECTS.map(([from, to]) => (
        <Route key={from} path={from} element={<Navigate to={to} replace />} />
      ))}

      {/* Catch-all route - Show 404 page */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

export default App